<template>
  <div id="posInvoices">

    <PageTitle :title="$t('pos.sales.number') + ' ' + (invoiceId || '...')"
      :subtitle="$t('pos.pos_invoices.pos_invoices_period')"></PageTitle>

    <div class="container">

      <Panel :showTitle="false">
        <div class="panel__title">{{ $t("invoice") }} {{ $t("pos.sales.number") }} {{ invoiceId || "..." }}
        </div>
        <div class="panel__description">
          {{ $t("sales.sale") }} {{ $t("pos.sales.number") }} {{ saleId || "..." }}
        </div>
        <div class="panel__section">
          <div class="section__title">{{ $t("pos.content_information") }}</div>
          <div class="panel__cards">
            <div class="cards__container">
              <div v-if="noSale" class="cards__nosale">{{ $t("pos.sales.could_not_load_details") }}</div>
              <!-- Items -->
              <div class="cards__card" :class="{ title: detailIndex === 0 }"
                v-for="(detail, detailIndex) in formattedDetails">
                <div class="card__title" v-if="detailIndex === 0">{{ $t("pos.items") }}</div>
                <div class="card__icon grey">
                  <svgicon src="icon/pos_photo.svg" :width="20" :height="20"></svgicon>
                </div>
                <div class="card__content">
                  <div class="card__row big">
                    <div class="row__value">{{ detail.name }}</div>
                  </div>
                  <div class="card__row modifier" v-for="modifier in detail.modifiers">
                    <div class="row__title">{{ modifier.name }}</div>
                    <div class="row__value" v-if="modifier.unitPrice">
                      {{ $tc(modifier.unitPrice, currency) }}
                    </div>
                  </div>
                  <div class="card__row modifier" v-for="promotion in detail.appliedPromotions">
                    <div class="row__title">{{ $to(promotion.name) }}</div>
                    <div class="row__value">
                      {{ $tc(-promotion.amount, currency) }}
                    </div>
                  </div>
                  <div class="card__row bottom">
                    <div class="row__title">
                      {{ $t("quantity") }} : <span class="big">{{ detail.quantity }}</span>
                    </div>
                    <div class="row__value">{{ $tc(getDetailTotal(detail), currency) }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="cards__container">
              <div class="cards__card title">
                <div class="card__title">{{ $t("sales.supplements") }}</div>
                <div class="card__icon">
                  <svgicon src="icon/pos_invoice.svg" :width="20" :height="20"></svgicon>
                </div>
                <div class="card__content">
                  <div class="card__row">
                    <div class="row__title">{{ $t("subTotal") }}</div>
                    <div class="row__value">{{ $tc(subTotal, currency) }}</div>
                  </div>
                  <div class="card__row modifier" v-for="tax in taxes">
                    <div class="row__title">{{ tax.name }}</div>
                    <div class="row__value">{{ $tc(tax.total, currency) }}</div>
                  </div>
                  <div class="card__row bottom">
                    <div class="row__title">{{ $t("total") }}</div>
                    <div class="row__value">{{ $tc(total, currency) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payments -->
        <div class="panel__section noborder">
          <div class="section__title">{{ $t("items.payment_information") }}</div>
          <div class="panel__cards">
            <div class="cards__container">
              <div class="cards__card title" v-for="payment in payments">
                <div class="card__title">{{ $t("sales.payments") }}</div>
                <div class="card__icon">
                  <svgicon src="icon/pos_payment.svg" :width="20" :height="20"></svgicon>
                </div>
                <div class="card__content">
                  <div class="card__row">
                    <div class="row__title">{{ $t("sales.payment_type") }}</div>
                    <div class="row__value">{{ $t("payment_method." + payment.method) }}</div>
                  </div>
                  <div class="card__row bottom">
                    <div class="row__title">{{ $t("sales.amount") }}</div>
                    <div class="row__value">{{ $tc(payment.amount, currency) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Panel>

      <PageFooter :backAction="{ action: backToDatatable, text: $t('pos.back_to_invoices') }"
        :mainAction="{ action: goToSale, text: $t('pos.access_sale') }">
      </PageFooter>

    </div>

  </div>
</template>

<script>
  import Datatable from "@/components/Datatable.vue";
  import { mapActions } from "vuex";
  import PageTitle from "@/components/PageTitle.vue";
  import PageFooter from "@/components/PageFooter.vue";

  export default {
    components: { Datatable, PageTitle, PageFooter },
    data() {
      return {
        entity: "invoice",
        invoice: null,
        sale: null,
        noSale: false,
        parameters: {
          company: this.$route.query.company,
          service: "pos"
        }
      };
    },
    mounted() {
      this.loadInvoice(this.$route.params.id);
    },
    computed: {
      invoiceId() {
        return this.invoice ? this.invoice.id : null;
      },
      saleId() {
        return this.sale ? this.sale.id : null;
      },
      payments() {
        return this.sale ? this.sale.payments : [];
      },
      currency() {
        return this.sale ? this.sale.currency : null;
      },
      subTotal() {
        return this.invoice ? this.invoice.subTotal : 0;
      },
      taxes() {
        return this.sale ? this.sale.taxes : [];
      },
      total() {
        return this.invoice ? this.invoice.total : 0;
      },
      details() {
        return this.sale ? this.sale.details : [];
      },
      company() {
        return this.$route.query.company || undefined;
      },
      formattedDetails() {
        // Add root items
        let details = this.details.filter(d => d.parentDetail === null);
        // Add modifiers to root items
        this.details.filter(d => d.parentDetail !== null).map(d => {
          let index = d.parentDetail;
          let parentDetail = details.filter(d => d.altId === index)[0];
          if (!parentDetail.modifiers) {
            parentDetail.modifiers = [];
          }
          parentDetail.modifiers.push(d);
        });
        return details;
      }
    },
    methods: {
      ...mapActions(["getInvoice", "getSale"]),
      async onRowClick(row) {
        this.$router.push(this.$router.buildContext("/pos-invoices") + "&invoice=" + row.invoiceNumber);
        this.loadInvoice(row.invoiceNumber);
      },
      async loadInvoice(invoiceNumber) {
        this.invoice = null;
        this.sale = null;
        this.noSale = false;
        showSpinner();
        this.invoice = await this.getInvoice({
          invoiceNumber,
          company: this.company
        });
        if (this.invoice === null) {
          hideSpinner();
          toast(this.$t("error.download_failed"), "error");
          return;
        }
        if (this.invoice.sale) {
          this.sale = await this.getSale({ saleId: this.invoice.sale, dto: "pos" });
        } else {
          this.noSale = true;
        }
        hideSpinner();
      },
      backToDatatable() {
        this.$router.push(this.$router.buildContext("/pos-invoices"));
      },
      getDetailTotal(detail) {
        let total = detail.unitPrice * detail.quantity;
        if (detail.modifiers) {
          for (let modifier of detail.modifiers) {
            total += modifier.unitPrice * modifier.quantity;
          }
        }
        // Remove promotions
        let promotionTotal = detail.appliedPromotions.reduce((total, promotion) => {
          return total + promotion.amount;
        }, 0) * detail.quantity;
        promotionTotal = Math.min(promotionTotal, Math.abs(total));
        total -= promotionTotal;
        return total;
      },
      goToSale() {
        this.$router.push(this.$router.buildContext("/pos-sales/" + this.saleId));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #posInvoices {
    display: flex;
    flex-direction: column;
  }

  .panel__section {
    &:not(:last-child) {
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px dashed #aaa;
    }

    &.noborder {
      border-bottom: none;
    }

    .section__title {
      color: #FF5500;
      font-weight: bold;
      font-size: 12px;
      margin-bottom: 4px;
    }
  }

  .panel__cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 32px;

    .cards__container {
      &> :not(:last-child) {
        margin-bottom: 8px;
      }
    }

    .cards__nosale {
      font-weight: bold;
      margin-top: 18px;
    }

    .cards__card {
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      padding: 10px;
      display: flex;
      position: relative;

      &.title {
        margin-top: 36px;
      }

      .card__title {
        font-size: 12px;
        color: #808080;
        position: absolute;
        top: -16px;
        left: 0;
      }

      .card__icon {
        width: 40px;
        height: 40px;
        background-color: #ED2937;
        border-radius: 6px;
        fill: white;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;

        &.grey {
          background-color: #F5F5F5;
          fill: #DBDBDB;
        }
      }

      .card__content {
        flex-grow: 1;
      }

      .card__row {
        display: flex;
        align-items: center;

        &.big {
          .row__value {
            font-size: 16px;
          }
        }

        &.modifier:not(:last-child) {
          border-bottom: none;
          margin-bottom: 0;

          .row__value {
            font-size: 12px;
            font-weight: bold;
            color: #808080;
          }

          &+.card__row.bottom {
            border-top: 1px solid #ddd;
            padding-top: 8px;
          }
        }

        &.bottom {
          .row__title .big {
            font-size: 16px;
            color: black;
          }

          .row__value {
            font-weight: bold;
          }
        }

        &:not(:last-child) {
          padding-bottom: 8px;
          margin-bottom: 8px;
          border-bottom: 1px solid #ddd;
        }

        .row__title {
          flex-grow: 1;
          font-size: 12px;
          color: #808080;
          margin-right: 10px;
          white-space: nowrap;
        }

        .row__value {
          font-size: 16px;
          text-align: right;

          .completed {
            color: #36D53D;
            display: flex;
            align-items: center;

            &:before {
              content: "";
              background-color: #36D53D;
              width: 8px;
              height: 8px;
              display: inline-block;
              border-radius: 50%;
              margin-right: 8px;
            }
          }
        }
      }
    }
  }

  .panel__title {
    font-size: 14px;
    font-weight: bold;

    &.margin {
      margin-bottom: 24px;
    }
  }

  .panel__description {
    font-size: 12px;
    color: #808080;
    margin-bottom: 24px;
  }
</style>